import {
	ShiftPurpose,
	ShiftPurposeDetailField,
} from "../components/gat-admin/store/gat-admin-slice-types";

/**
 * Length limit applied to a new question's answers. The server owns this and
 * returns it as `detailField.maxLength` — always prefer the value that came
 * back with the purpose, and use this only when configuring a brand new one.
 */
export const SHIFT_PURPOSE_DETAIL_DEFAULT_MAX_LENGTH = 250;

/**
 * Shift Purposes to offer while posting or editing a Shift, keyed by id.
 */
export const getShiftPurposeOptions = (
	shiftPurposes: Record<string, ShiftPurpose> | undefined
): Record<string, ShiftPurpose> => shiftPurposes ?? {};

/**
 * The free-text detail the selected Shift Purpose asks for, as configured in GAT
 * Admin — e.g. the purpose "Overtime" asks for a "Cost centre". Null when the
 * purpose asks for nothing, which is the case for most of them.
 */
export const getShiftPurposeDetailField = (
	shiftPurposes: Record<string, ShiftPurpose> | undefined,
	shiftPurposeId: number | string
): ShiftPurposeDetailField | null => {
	if (!shiftPurposeId) return null;
	return shiftPurposes?.[`${shiftPurposeId}`]?.detailField ?? null;
};

export interface ShiftIncompatibility {
	missingPurposeDetail?: { label: string; purposeName: string };
}

/**
 * Why a Shift can't be published as it stands, or null when nothing blocks it.
 * A Draft made before its purpose's question became required has no answer,
 * and publishing rejects it until a supervisor edits the Shift. Add future
 * required fields here so every surface flagging these Shifts picks them up.
 */
export const getShiftIncompatibility = (
	shift: {
		shiftPurposeId?: number | string | null;
		shiftPurposeDetail?: string | null;
	},
	shiftPurposes: Record<string, ShiftPurpose> | undefined
): ShiftIncompatibility | null => {
	const detailField = getShiftPurposeDetailField(
		shiftPurposes,
		shift.shiftPurposeId ?? 0
	);
	if (!detailField?.isRequired || shift.shiftPurposeDetail?.trim()) {
		return null;
	}
	return {
		missingPurposeDetail: {
			label: detailField.label,
			purposeName: shiftPurposes?.[`${shift.shiftPurposeId}`]?.name ?? "",
		},
	};
};
